import {
    adoptSimClockStateId,
    getKnownSimClockStateId,
    isLocalClockMutationInProgress,
    setSimClockStale,
} from './app-clock.js'
import { isQaOverlayEnabledForCurrentEnvironment } from './qa-dev-tools-gating.js'

/** Must match API `SIM_CLOCK_ID_HEADER`. */
export const SIM_CLOCK_ID_HEADER = 'X-Lodgr-Sim-Clock-Id'

/** Must match API `SIM_CLOCK_STATE_HEADER`. */
export const SIM_CLOCK_STATE_HEADER = 'X-Lodgr-Sim-Clock-State'

/** True when QA sim-clock drift headers should be sent and interpreted. */
export const isSimClockDriftEnabled = () => isQaOverlayEnabledForCurrentEnvironment()

/**
 * @param {string|null|undefined} knownStateId
 * @param {string} responseStateId
 * @returns {{ isStale: boolean, adoptedStateId: string }}
 */
export const evaluateSimClockDrift = (knownStateId, responseStateId) => {
    if (!responseStateId) {
        throw new Error(
            'Missing X-Lodgr-Sim-Clock-State on API response — sim-clock drift headers are not enabled on the server',
        )
    }
    if (!knownStateId) {
        return {
            adoptedStateId: responseStateId,
            isStale: false,
        }
    }
    if (knownStateId !== responseStateId) {
        return {
            adoptedStateId: knownStateId,
            isStale: true,
        }
    }
    return {
        adoptedStateId: responseStateId,
        isStale: false,
    }
}

/**
 * @param {string|null|undefined} knownStateId
 * @returns {Record<string, string>}
 */
export const buildSimClockDriftRequestHeaders = (knownStateId) => {
    if (!knownStateId) {
        return {}
    }
    return {
        [SIM_CLOCK_ID_HEADER]: knownStateId,
    }
}

/**
 * Compare the echoed server state id against the client's last-known id.
 * @param {Response} response
 */
export const applySimClockDriftFromResponse = (response) => {
    if (!isSimClockDriftEnabled()) {
        return
    }

    const responseStateId = response.headers.get(SIM_CLOCK_STATE_HEADER)
    const knownStateId = getKnownSimClockStateId()

    if (knownStateId && !responseStateId) {
        throw new Error(
            'Missing X-Lodgr-Sim-Clock-State on API response — sim-clock drift headers are not enabled on the server',
        )
    }
    if (!responseStateId) {
        return
    }

    // The tester is changing the clock themselves: the new server state id is
    // expected, not external drift. Adopt it as the new baseline (and clear any
    // stale flag via adopt) instead of raising a false de-sync warning.
    if (isLocalClockMutationInProgress()) {
        adoptSimClockStateId(responseStateId)
        return
    }

    const result = evaluateSimClockDrift(knownStateId, responseStateId)
    if (result.isStale) {
        setSimClockStale(true)
        return
    }
    adoptSimClockStateId(result.adoptedStateId)
}
